import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../ui/tooltip';
import { AlertCircle, Info, ScanSearch, ArrowRight, HelpCircle } from 'lucide-react';
import { parseDtcInput, EXAMPLE_INPUTS } from '../../../lib/dtcDecoder/parser';
import { hex2 } from '../../../lib/dtcDecoder/constants';
import { getNrcEntry, getSidName } from '../../../lib/nrcDecoder/constants';
import DtcCodePanel from './DtcCodePanel';
import StatusBytePanel from './StatusBytePanel';
import UdsResponsePanel from './UdsResponsePanel';
import ObdPanel from './ObdPanel';
import DtcReferenceTables from './DtcReferenceTables';
import HowDtcsWork, { WORKED_EXAMPLE } from './HowDtcsWork';
import { WarningList } from './shared';

const TABS = [
  { value: 'dtc', label: 'DTC Code' },
  { value: 'status', label: 'Status Byte' },
  { value: 'uds', label: 'UDS 0x19' },
  { value: 'obd', label: 'OBD-II' },
  { value: 'reference', label: 'Reference' },
];

const KIND_LABEL = {
  dtcCode: 'DTC code',
  dtcBytes: 'DTC bytes',
  ftb: 'Failure Type Byte',
  status: 'DTC status byte',
  uds: 'UDS 0x19 / 0x14 message',
  obd: 'OBD-II message',
};

const DtcDecoder = () => {
  const [input, setInput] = useState('');
  const [tab, setTab] = useState('dtc');
  const [detected, setDetected] = useState(null);
  const [seeds, setSeeds] = useState({});

  const seed = (key, value) => setSeeds((s) => ({ ...s, [key]: { ...value, nonce: Date.now() + Math.random() } }));

  const decode = (raw, opts = {}) => {
    const text = raw !== undefined ? raw : input;
    const r = parseDtcInput(text, opts);
    setDetected({ ...r, text });
    if (!r.ok) return;
    switch (r.kind) {
      case 'dtcCode':
      case 'dtcBytes':
        seed('dtc', { text });
        setTab('dtc');
        break;
      case 'ftb':
        seed('dtc', { text, ftbOnly: r.ftb });
        setTab('dtc');
        break;
      case 'status':
        seed('status', { value: r.status });
        setTab('status');
        break;
      case 'uds':
        seed('uds', { text });
        setTab('uds');
        break;
      case 'obd':
        seed('obd', { text, parsed: r });
        setTab('obd');
        break;
      default:
        break;
    }
  };

  const loadExample = (value) => {
    setInput(value);
    decode(value);
  };

  const nrcEntry = detected?.ok && detected.kind === 'nrc' ? getNrcEntry(detected.nrc) : null;

  return (
    <div className="space-y-6" data-testid="dtc-decoder">
      <TooltipProvider delayDuration={150}>
        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Smart Decode</CardTitle>
            <CardDescription>Paste anything DTC-related — the tool detects what it is and opens the right tab.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); decode(); }} className="space-y-2">
              <div className="flex items-center gap-1.5">
                <label htmlFor="dtc-smart-input" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">DTC, status byte, UDS or OBD-II response</label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid="dtc-input-tip" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    Accepts P0301 / P0A1F-1C, spaced, comma-separated, 0x-prefixed or contiguous hex, an optional CAN ID (7E8 …) and an optional ISO-TP length byte.
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  id="dtc-smart-input"
                  className="font-mono"
                  placeholder="Paste a DTC, status byte, UDS or OBD-II response…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  data-testid="dtc-smart-input"
                />
                <Button type="submit" className="btn-premium shrink-0" data-testid="dtc-smart-decode">
                  <ScanSearch className="h-4 w-4" /> Decode
                </Button>
              </div>
            </form>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground mr-1">Examples:</span>
              {[...EXAMPLE_INPUTS, { label: 'Worked example', value: WORKED_EXAMPLE }].map((ex) => (
                <Badge
                  key={ex.label}
                  variant="outline"
                  className="font-mono text-[11px] font-normal cursor-pointer hover:bg-accent py-1"
                  onClick={() => loadExample(ex.value)}
                  title={ex.value}
                  data-testid={`dtc-example-${ex.label.replace(/[\s]+/g, '-')}`}
                >
                  {ex.label}
                </Badge>
              ))}
            </div>

            {detected && !detected.ok && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="dtc-input-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{detected.error}</span>
              </div>
            )}

            {detected?.ok && detected.kind === 'ambiguousByte' && (
              <div className="rounded-md border border-amber-600/40 bg-amber-600/10 px-3 py-3 space-y-2" data-testid="dtc-ambiguous">
                <p className="text-sm flex items-start gap-2"><HelpCircle className="h-4 w-4 shrink-0 mt-0.5" /> A single byte (0x{hex2(detected.value)}) could mean several things. Which is it?</p>
                <div className="flex flex-wrap gap-2">
                  {detected.options.map((o) => (
                    <Button key={o.label} variant="outline" size="sm" className="h-auto min-h-[36px] whitespace-normal text-left" onClick={() => decode(detected.text, { force: o.force })} data-testid={`dtc-ambiguous-${o.force}`}>
                      {o.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {detected?.ok && detected.kind === 'nrc' && (
              <div className="rounded-md border border-structure bg-muted/40 px-3 py-3 space-y-1.5 text-sm" data-testid="dtc-nrc-handoff">
                <p><span className="font-semibold">Negative response:</span> service 0x{hex2(detected.sid)}{getSidName(detected.sid) ? ` (${getSidName(detected.sid)})` : ''} was rejected with NRC 0x{hex2(detected.nrc)} — <span className="font-semibold">{nrcEntry.label}</span>.</p>
                <p className="text-xs text-muted-foreground">{nrcEntry.meaning}</p>
                <Link to={`/tools/diagnostic/nrc-decoder?input=${encodeURIComponent(`7F ${hex2(detected.sid)} ${hex2(detected.nrc)}`)}`} className="inline-flex items-center gap-1 text-sm font-medium text-autoxium-blue hover:underline" data-testid="dtc-nrc-link">
                  Open in the NRC Decoder for causes and troubleshooting <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}

            {detected?.ok && KIND_LABEL[detected.kind] && (
              <div className="space-y-2 text-xs" data-testid="dtc-detected">
                <p className="text-muted-foreground">
                  Detected: <span className="font-semibold text-foreground" data-testid="dtc-detected-kind">{KIND_LABEL[detected.kind]}</span>
                  {detected.canInfo && <> · CAN ID <span className="font-mono" data-testid="dtc-detected-can">{detected.canInfo.text}</span></>}
                  {detected.isoTp && <> · ISO-TP length byte 0x{hex2(detected.isoTp.length)} removed</>}
                </p>
                <WarningList items={detected.warnings} testId="dtc-detected-warnings" />
                {detected.alternatives?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-muted-foreground">Not what you meant?</span>
                    {detected.alternatives.map((a) => (
                      <Button key={a.label} variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => (a.forceText ? loadExample(a.forceText) : decode(detected.text, { force: a.force }))} data-testid={`dtc-alt-${a.force || 'text'}`}>
                        {a.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </TooltipProvider>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="h-auto flex-wrap justify-start w-full gap-1" data-testid="dtc-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="flex-1 min-w-[96px] min-h-[36px]" data-testid={`dtc-tab-${t.value}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dtc" forceMount className="data-[state=inactive]:hidden">
          <DtcCodePanel seed={seeds.dtc} onOpenStatus={(v) => { seed('status', { value: v }); setTab('status'); }} />
        </TabsContent>
        <TabsContent value="status" forceMount className="data-[state=inactive]:hidden">
          <StatusBytePanel seed={seeds.status} />
        </TabsContent>
        <TabsContent value="uds" forceMount className="data-[state=inactive]:hidden">
          <UdsResponsePanel seed={seeds.uds} />
        </TabsContent>
        <TabsContent value="obd" forceMount className="data-[state=inactive]:hidden">
          <ObdPanel seed={seeds.obd} />
        </TabsContent>
        <TabsContent value="reference" forceMount className="data-[state=inactive]:hidden">
          <DtcReferenceTables onPickCode={(code) => { setInput(code); decode(code); }} />
        </TabsContent>
      </Tabs>

      <HowDtcsWork onTryExample={(v) => { loadExample(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
    </div>
  );
};

export default DtcDecoder;
